import { Avatar, Button, MenuItem, MenuList, mergeClasses } from "@fluentui/react-components";
import React, { use, useId, useMemo } from "react";

import { SearchResultItem } from "./M365PulseSearchBox.types";
import { observer } from "mobx-react";
import { usePersonImages, useSmallScreen } from "Shared/Hooks";
import { useStyles } from "./M365PulseSearchBox.styles";
import { useCategoryPopoverStyles } from "./M365PulseSearchBoxCategoryPopover.styles";
import { useRootStore } from "Store/RootStore";
import { SearchContext } from "./SearchStore";
import { useNavigate } from "react-router";
import { AppRegistry } from "Apps/AppRegistry";
import { useGlobalSetting } from "Store/GlobalSettingStore";
import { Dismiss12Regular, Search20Regular } from "@fluentui/react-icons";
import { TeamAppConfig } from "Apps/TeamAppModel";
import { useSearchResultKeyDown } from "./useSearchResultKeyDown";

const MemoizedAvatar: React.FC<{ imageSrc?: string; title: string }> = ({ imageSrc, title }) => {
    const imageProp = imageSrc ? { src: imageSrc } : undefined;
    return <Avatar image={imageProp} aria-label={title} name={title} />;
};

interface M365PulseSearchBoxCategoryPopoverProps {
    searchResultsId?: string;
}

const M365PulseSearchBoxCategoryPopoverBase = ({ searchResultsId }: M365PulseSearchBoxCategoryPopoverProps) => {
    const navigate = useNavigate();
    const userSearchSettings = useGlobalSetting("userSearchSettings");
    const store = use(SearchContext);
    const universalSearchStore = useRootStore("UniversalSearchStore");
    const classNames = useStyles();
    const popoverStyles = useCategoryPopoverStyles();
    const isSmallScreen = useSmallScreen();
    const descStyles = mergeClasses(classNames.categoryDesc, isSmallScreen ? classNames.categoryDescSmall : classNames.categoryDescLarge);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const resultMap = useMemo(() => (store ? store.getSearchResultMap() : new Map<string, SearchResultItem[]>()), [store?.searchResultList]);

    const flatIndexById = useMemo(() => {
        const map = new Map<string, number>();
        store?.getEnhancedSearchResults().forEach((item, index) => {
            map.set(item.id, index);
        });
        return map;
    }, [store]);

    const searchResultMap = useMemo(() => {
        const map = new Map<string, SearchResultItem[]>();
        resultMap.forEach((items, category) => {
            const modifiedItems = items.map((item: SearchResultItem) => {
                const newItem = { ...item };
                if (category.toLowerCase() === "recent searches" && item.originalCategory?.toLowerCase() === "apps" && item.navigationTarget) {
                    const appEntry = Object.entries(AppRegistry).find(([_, app]: [string, TeamAppConfig]) => app.path === item.navigationTarget);

                    if (appEntry) {
                        const foundApp: TeamAppConfig = appEntry[1];
                        newItem.icon = foundApp.filledIcon;
                    }
                }

                if (category.toLowerCase() === "trending searches" && item.navigationTarget) {
                    const pathSegments = item.navigationTarget.split("/");
                    if (pathSegments.length >= 2) {
                        const appName = pathSegments[1];
                        const appPath = `/${appName}`;

                        const appEntry = Object.entries(AppRegistry).find(([_, app]: [string, TeamAppConfig]) => app.path === appPath);

                        if (appEntry) {
                            const foundApp: TeamAppConfig = appEntry[1];
                            newItem.icon = foundApp.filledIcon;
                        } else {
                            newItem.icon = Search20Regular;
                        }
                    } else {
                        newItem.icon = Search20Regular;
                    }
                }

                return {
                    ...newItem,
                    onClick: () => {
                        if (
                            !userSearchSettings.findItem(item.originalCategory ? item.originalCategory : category, item.id) &&
                            item.category === "Recent Searches"
                        ) {
                            return;
                        }
                        item.onClick?.();
                        if (item.navigationTarget) {
                            navigate(item.navigationTarget);
                            userSearchSettings.addItem({
                                id: item.id,
                                title: item.title ? item.title : "",
                                description: item.description || "",
                                icon: item.icon,
                                appPath: item.navigationTarget,
                                category: item.category,
                                originalCategory: item.originalCategory,
                                navigationTarget: item.navigationTarget
                            });
                        }
                        if (item.category !== "Filters" && item.category !== "") {
                            universalSearchStore.searchWord = "";
                        }
                    },
                    originalCategory: item.originalCategory ? item.originalCategory : category
                };
            });
            map.set(category, modifiedItems);
        });
        return map;
    }, [resultMap, universalSearchStore, navigate, userSearchSettings]);

    const contactAliases = useMemo(() => {
        const aliases = new Set<string>();
        [...searchResultMap.values()].forEach((items) => {
            items.forEach((item) => {
                if (
                    (item.category?.toLowerCase() === "contacts" ||
                        item.category?.toLowerCase() === "top results" ||
                        item.category?.toLowerCase() === "recent searches") &&
                    item.description
                ) {
                    aliases.add(item.description);
                }
            });
        });
        return Array.from(aliases);
    }, [searchResultMap]);

    const aliasToImageMap = usePersonImages(contactAliases);

    const onMenuItemKeyDown = useSearchResultKeyDown();
    const categoryIdPrefix = useId();

    return (
        <MenuList role="presentation" aria-labelledby={undefined} className={classNames.category}>
            {[...searchResultMap].map(([key, items], categoryIndex) => (
                <div key={key} className={classNames.categoryRow} role="group" aria-labelledby={`${categoryIdPrefix}-${categoryIndex}`}>
                    <div id={`${categoryIdPrefix}-${categoryIndex}`} className={classNames.categoryName}>
                        {key}
                    </div>
                    <div className={classNames.categoryResults}>
                        {items.map((item) => (
                            <MenuItem
                                key={key + item.id}
                                id={searchResultsId !== undefined ? `${searchResultsId}-opt-${flatIndexById.get(item.id) ?? 0}` : undefined}
                                onClick={item.onClick}
                                onKeyDown={(e) => onMenuItemKeyDown(e, item)}
                                className={classNames.categoryResult}
                                role="option"
                                aria-keyshortcuts={item.deleteItemFromHistory ? "Delete" : undefined}
                            >
                                <div className={popoverStyles.resultContainer}>
                                    <div className={popoverStyles.resultRow}>
                                        {item.originalCategory?.toLowerCase() === "contacts" && item.description ? (
                                            <span className={popoverStyles.resultIcon}>
                                                <MemoizedAvatar
                                                    imageSrc={aliasToImageMap.aliasPhotoMap.get(item.description)}
                                                    title={item.title ? item.title : ""}
                                                />
                                            </span>
                                        ) : item.icon ? (
                                            <span className={popoverStyles.resultIcon}>
                                                <item.icon />
                                            </span>
                                        ) : null}
                                        <div className={popoverStyles.resultText}>
                                            <div className={classNames.categoryTitle}>{item.title}</div>
                                            <div className={descStyles}>{item.description}</div>
                                        </div>

                                        {key.toLowerCase() === "recent searches" && (
                                            <Button
                                                icon={<Dismiss12Regular />}
                                                onClick={item.deleteItemFromHistory}
                                                title="Remove from recent searches"
                                                aria-hidden
                                                tabIndex={-1}
                                                appearance="subtle"
                                                size="small"
                                                className={mergeClasses(popoverStyles.deleteButton, "delete-button")}
                                            />
                                        )}
                                    </div>
                                </div>
                            </MenuItem>
                        ))}
                    </div>
                </div>
            ))}
        </MenuList>
    );
};

export const M365PulseSearchBoxCategoryPopover = observer(M365PulseSearchBoxCategoryPopoverBase);
